import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { ClinicDialog } from './ClinicDialog';
import { CLINIC_DIALOG_SX } from '../../constants/popupStyles';
import { MeetingType, UserType } from '../../services/meetingTypeService';
import { Patient } from '../../services/patientService';
import { userService, User } from '../../services/userService';

interface YomanSchedulingDialogProps {
  open: boolean;
  onClose: () => void;
  patient?: Patient | null;
  meetingTypes: MeetingType[];
  loading?: boolean;
  currentTenantId?: number;
  onCreateInvite?: (meetingTypeId: number, therapistId?: number) => Promise<void>;
}

export const YomanSchedulingDialog: React.FC<YomanSchedulingDialogProps> = ({
  open,
  onClose,
  patient,
  meetingTypes = [],
  loading = false,
  currentTenantId,
  onCreateInvite,
}) => {
  const [selectedMeetingTypeId, setSelectedMeetingTypeId] = useState<number | ''>('');
  const [selectedTherapistId, setSelectedTherapistId] = useState<number | ''>('');
  const [isCreatingInvite, setIsCreatingInvite] = useState(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [availableOwners, setAvailableOwners] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Fetch all users when dialog opens
  useEffect(() => {
    if (!open || !currentTenantId) {
      return;
    }

    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const users = await userService.getUsers(currentTenantId);
        setAllUsers(users);
      } catch (error) {
        console.error('Error fetching users:', error);
        setAllUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [open, currentTenantId]);

  // Default meeting type from patient.next_meeting_type_id when dialog opens
  useEffect(() => {
    if (!open || !patient || meetingTypes.length === 0) return;
    if (patient.next_meeting_type_id) {
      const defaultType = meetingTypes.find(mt => mt.id === patient.next_meeting_type_id);
      if (defaultType) {
        setSelectedMeetingTypeId(defaultType.id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patient?.id, meetingTypes]);

  // Default the THERAPIST from the patient's care team, by what the chosen
  // meeting type needs - a doctor for a psychiatric meeting, a case manager
  // for a case-management one.
  //
  // Re-run on every meeting type change, because the right clinician depends
  // on the type: leaving the previous one selected would book a psychiatric
  // appointment with a case manager.
  //
  // Without this the dialog sent no therapist at all unless one was picked
  // by hand, and the scheduling page then fell back to the pool - so a
  // clinician who had chosen a patient's own therapist got whoever was free.
  useEffect(() => {
    if (!open || !patient) return;
    const type = meetingTypes.find((mt) => mt.id === selectedMeetingTypeId);
    if (!type) return;
    const assigned = {
      doctor: patient.doctor_id,
      case_manager: patient.case_manager_id,
      nurse_prescriber: patient.nurse_prescriber_id,
    }[String(type.user_type)];
    // No assignment means the pool, which is a real answer - so clear it
    // rather than leaving whoever was selected for the previous type.
    setSelectedTherapistId(assigned || '');
  }, [open, patient, meetingTypes, selectedMeetingTypeId]);

  // Filter owners and set default owner based on meeting type + patient's doctor/case_manager
  useEffect(() => {
    if (!selectedMeetingTypeId) {
      setAvailableOwners([]);
      setSelectedTherapistId('');
      return;
    }

    const selectedMeeting = meetingTypes.find(mt => mt.id === selectedMeetingTypeId);
    if (!selectedMeeting) {
      setAvailableOwners([]);
      return;
    }

    // Filter users by their assigned meeting types (meeting_type_ids must include the selected
    // meeting type), and they must have a Yoman ID — not by user_type.
    const filtered = allUsers.filter(
      user => (user.meeting_type_ids || []).includes(selectedMeeting.id) && user.yoman_id
    );
    setAvailableOwners(filtered);

    // Default owner: the patient's care-team member matching the meeting type's user type
    let defaultOwnerId: number | '' = '';
    if (patient) {
      if (selectedMeeting.user_type === UserType.DOCTOR && patient.doctor_id) {
        defaultOwnerId = patient.doctor_id;
      } else if (selectedMeeting.user_type === UserType.CASE_MANAGER && patient.case_manager_id) {
        defaultOwnerId = patient.case_manager_id;
      } else if (selectedMeeting.user_type === UserType.NURSE_PRESCRIBER && patient.nurse_prescriber_id) {
        defaultOwnerId = patient.nurse_prescriber_id;
      }
    }
    // Only keep the default if that user is actually eligible for this meeting type.
    if (defaultOwnerId && !filtered.some(u => u.id === defaultOwnerId)) {
      defaultOwnerId = '';
    }
    setSelectedTherapistId(defaultOwnerId);
  }, [selectedMeetingTypeId, meetingTypes, allUsers, patient]);

  const handleCreate = async () => {
    if (!selectedMeetingTypeId || !onCreateInvite) {
      return;
    }

    try {
      setIsCreatingInvite(true);

      await onCreateInvite(
        Number(selectedMeetingTypeId),
        selectedTherapistId ? Number(selectedTherapistId) : undefined
      );
    } catch (error) {
      console.error('Error creating Yoman invite:', error);
      throw error;
    } finally {
      setIsCreatingInvite(false);
    }
  };

  const handleClose = () => {
    setSelectedMeetingTypeId('');
    setSelectedTherapistId('');
    setIsCreatingInvite(false);
    onClose();
  };

  const selectSx = (hasValue: boolean) => ({
    ...CLINIC_DIALOG_SX.select,
    color: hasValue ? CLINIC_DIALOG_SX.fieldLabel.color : '#999',
  });

  const actions = (
    <>
      <Button
        type="button"
        onClick={handleCreate}
        disabled={!selectedMeetingTypeId || isCreatingInvite}
        sx={CLINIC_DIALOG_SX.primaryButton}
      >
        {isCreatingInvite ? 'Opening...' : 'Create'}
      </Button>
      <Button
        type="button"
        onClick={handleClose}
        disabled={isCreatingInvite}
        sx={CLINIC_DIALOG_SX.cancelButton}
      >
        Cancel
      </Button>
    </>
  );

  return (
    <ClinicDialog
      open={open}
      onClose={handleClose}
      title="Create Meeting"
      loading={isCreatingInvite}
      actions={actions}
    >
      {/* Patient Info */}
      {patient && (
        <Typography variant="body2" sx={{ ...CLINIC_DIALOG_SX.infoText, mb: 3, textAlign: 'center' }}>
          {patient.full_name} - {patient.hm_id || 'No HM ID'}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Meeting Type Dropdown */}
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
            Meeting Type
          </Typography>
          <Select
            value={selectedMeetingTypeId}
            onChange={(e) => setSelectedMeetingTypeId(e.target.value as number | '')}
            displayEmpty
            fullWidth
            sx={selectSx(!!selectedMeetingTypeId)}
          >
            <MenuItem value="" disabled>
              Select an option
            </MenuItem>
            {meetingTypes.map((mt) => (
              <MenuItem key={mt.id} value={mt.id}>
                {mt.name}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Owner Dropdown (Optional) */}
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
            Owner
          </Typography>
          <Select
            value={selectedTherapistId}
            onChange={(e) => setSelectedTherapistId(e.target.value as number | '')}
            displayEmpty
            fullWidth
            disabled={!selectedMeetingTypeId || loadingUsers || availableOwners.length === 0}
            sx={selectSx(!!selectedTherapistId)}
          >
            <MenuItem value="">
              {loadingUsers ? 'Loading owners...' : 'None (Therapist pool)'}
            </MenuItem>
            {availableOwners.map((owner) => (
              <MenuItem key={owner.id} value={owner.id}>
                {owner.full_name}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
    </ClinicDialog>
  );
};
